import React, { useMemo, useState } from "react";
import { Button, Badge, Spinner, Modal, Card } from "react-bootstrap";
import { FaEye, FaCheck } from "react-icons/fa";
import { VscDebugRestart } from "react-icons/vsc";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../context/AuthContext";
import { approveTickets, reopenTicket } from "../../api/TICKET/ticket";

// ---------------------------------------------------------------------------
// Status display maps — centralised so changes only happen in one place.
// ---------------------------------------------------------------------------
const STATUS_LABELS = { closed: "Clôturé", approved: "Approuvé" };
const STATUS_BADGE  = { closed: "danger",  approved: "success"  };

// Shared style for fixed-size icon buttons.
const BTN_STYLE = {
  width: "32px", height: "32px", padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const TicketsFermesRow = ({ ticket, listState, asCard = false }) => {
  // ── Router ────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const location = useLocation();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { hasPermission } = useAuth();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [showConfirmReopen,  setShowConfirmReopen]  = useState(false);
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);

  // ── Remote mutations ──────────────────────────────────────────────────────
  const queryClient = useQueryClient();

  const reopenMutation = useMutation({
    mutationFn: reopenTicket,
    onSuccess: () => {
      toast.success("Ticket réouvert avec succès");
      setShowConfirmReopen(false);
      queryClient.invalidateQueries({ queryKey: ["inactiveTickets"] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      navigate(`/ticket/${ticket.id}`);
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la réouverture du ticket");
    },
  });

  const approveMutation = useMutation({
  mutationFn: approveTickets,
  onSuccess: () => {
    toast.success("Ticket approuvé avec succès");
    setShowConfirmApprove(false);
    queryClient.invalidateQueries({ queryKey: ["inactiveTickets"] });
    queryClient.invalidateQueries({ queryKey: ["ticketsApproval"] });
  },
  onError: (error) => {
    toast.error(error.message || "Erreur lors de l'approbation du ticket");
  },
});

  // ── Derived values ────────────────────────────────────────────────────────
  const sortedInterventions = useMemo(() => {
    const interventions = ticket.ticket_interventions || [];
    return [...interventions].sort((a, b) => {
      const aTime = new Date(a?.start_date || a?.created_at || 0).getTime();
      const bTime = new Date(b?.start_date || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [ticket]);
  const rawStatus     = ticket.rawStatus || "";
  const statusLabel   = STATUS_LABELS[rawStatus] || ticket.statut || rawStatus || "Non assigné";
  const statusBadge   = STATUS_BADGE[rawStatus]  || "secondary";
  const billingBadgeVariant = rawStatus === "closed" ? "success" : rawStatus === "approved" ? "secondary" : null;

  // Actions visible only for "closed" tickets with appropriate permissions.
  const showReopen  = rawStatus === "closed" && hasPermission("OPEN_TICKET");
  const showApprove = rawStatus === "closed" && hasPermission("APPROVE_TICKET");

  // ── Helpers ───────────────────────────────────────────────────────────────

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleString("fr-FR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  // Returns a colored <span> based on how old the intervention is.
  const formatInterventionDate = (value) => {
    if (!value) return "N/A";
    try {
      const d        = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return "N/A";
      const diffDays = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
      const cls      = diffDays > 10 ? "text-danger" : diffDays > 5 ? "text-warning" : "";
      return (
        <span className={cls}>
          {d.toLocaleString("fr-FR", {
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
          })}
        </span>
      );
    } catch {
      return "N/A";
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleNavigate = () => {
    navigate(`/ticket/${ticket.id}`, {
      state: {
        from: location.pathname,
        listState: { ...listState, scrollY: window.scrollY },
      },
    });
  };

  const handleReopen = () => reopenMutation.mutate(ticket.id);

  const handleApprove = () => approveMutation.mutate([ticket.id]);

  // ── Sub-components ────────────────────────────────────────────────────────

  // Confirmation modals — shared between card and table views.
  const modals = (
    <>
      {/* Reopen confirmation */}
      <Modal show={showConfirmReopen} onHide={() => setShowConfirmReopen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la réouverture</Modal.Title>
        </Modal.Header>
        <Modal.Body>Êtes-vous sûr de vouloir réouvrir le ticket #{ticket.id} ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmReopen(false)}>Annuler</Button>
          <Button variant="warning" onClick={handleReopen} disabled={reopenMutation.isPending}>
            {reopenMutation.isPending ? "Réouverture..." : "Réouvrir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve confirmation */}
      <Modal show={showConfirmApprove} onHide={() => setShowConfirmApprove(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer l'approbation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Êtes-vous sûr de vouloir approuver le ticket #{ticket.id} ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmApprove(false)}>Annuler</Button>
          <Button
              variant="success"
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? "Approbation..." : "Approuver"}
            </Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  // Intervention list — shared between card and table views.
  const interventionList = sortedInterventions.length > 0
    ? sortedInterventions.map((intervention, idx) => (
        <div
          key={intervention.id || idx}
          style={{
            padding: "2px 0",
            borderBottom: idx < sortedInterventions.length - 1 ? "1px solid #ddd" : "none",
            fontSize: "0.875rem",
          }}
        >
          {formatInterventionDate(intervention.start_date || intervention.created_at)}
        </div>
      ))
    : null;

  // Action buttons — shared between card and table views.
  const actionButtons = (
    <>
      <Button
        variant="secondary"
        size="sm"
        title="Voir le ticket"
        style={BTN_STYLE}
        onClick={handleNavigate}
      >
        <FaEye />
      </Button>

      {showReopen && (
        <Button
          variant="warning"
          size="sm"
          title="Réouvrir le ticket"
          style={BTN_STYLE}
          onClick={() => setShowConfirmReopen(true)}
          disabled={reopenMutation.isPending}
        >
          {reopenMutation.isPending
            ? <Spinner animation="border" size="sm" />
            : <VscDebugRestart style={{ color: "white" }} />}
        </Button>
      )}

      {showApprove && (
      <Button
        variant="success"
        size="sm"
        title="Approuver le ticket"
        style={BTN_STYLE}
        onClick={() => setShowConfirmApprove(true)}
        disabled={approveMutation.isPending}
      >
        {approveMutation.isPending
          ? <Spinner animation="border" size="sm" />
          : <FaCheck />}
      </Button>
    )}
    </>
  );

  // ── Render: Mobile card ───────────────────────────────────────────────────
  if (asCard) {
    return (
      <>
        <Card className="h-100 shadow-sm">
          <Card.Body className="d-flex flex-column gap-2">

            {/* Header row: ID + status badge */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">#{ticket.id}</span>
              <Badge bg={statusBadge}>{statusLabel}</Badge>
            </div>

            <div className="fw-bold" style={{ fontSize: "0.95rem" }}>
              {ticket.titre || ticket.title}
            </div>

            <div className="small text-muted">
              <span className="fw-semibold">Début :</span>{" "}
              {formatDate(ticket.dateDebut || ticket.created_at)}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Client :</span> {ticket.client}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Intervenant(s) :</span>{" "}
              {Array.isArray(ticket.intervenants) && ticket.intervenants.length > 0
                ? ticket.intervenants.join(", ")
                : <span className="text-muted fst-italic">Non assigné</span>}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Intervention(s) :</span>
              {interventionList
                ? <div className="mt-1" style={{ maxHeight: "70px", overflowY: "auto" }}>{interventionList}</div>
                : <span className="text-muted fst-italic ms-1">Aucune</span>}
            </div>

           <div className="small">
            <span className="fw-semibold text-muted">Facturation :</span>{" "}
            {billingBadgeVariant ? (
              <Badge bg={billingBadgeVariant}>{ticket.billingMethod || "—"}</Badge>
            ) : (
              <span>{ticket.billingMethod || "—"}</span>
            )}
          </div>

            {/* Action buttons */}
            <div className="d-flex gap-2 mt-auto pt-2 border-top flex-wrap">
              {actionButtons}
            </div>

          </Card.Body>
        </Card>
        {modals}
      </>
    );
  }

  // ── Render: Desktop table row ─────────────────────────────────────────────
  return (
    <>
      <tr>
        <td className="align-middle">{ticket.id}</td>
        <td className="align-middle">{ticket.titre || ticket.title}</td>
        <td className="align-middle">{formatDate(ticket.dateDebut || ticket.created_at)}</td>
        <td className="align-middle">{ticket.client}</td>

        <td className="align-middle">
          {Array.isArray(ticket.intervenants) && ticket.intervenants.length > 0
            ? ticket.intervenants.join(", ")
            : "Non assigné"}
        </td>

        <td className="align-middle">
          <div style={{ maxHeight: "80px", overflowY: "auto" }}>
            {interventionList ?? <span className="text-muted">Aucune intervention</span>}
          </div>
        </td>

        <td className="align-middle text-center">
        {billingBadgeVariant ? (
          <Badge bg={billingBadgeVariant}>{ticket.billingMethod || "—"}</Badge>
        ) : (
          <span>{ticket.billingMethod || "—"}</span>
        )}
      </td>

        <td className="align-middle">
          <div className="d-flex gap-2 justify-content-center">
            {actionButtons}
          </div>
        </td>
      </tr>
      {modals}
    </>
  );
};

export default TicketsFermesRow;